import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Badge, Checkbox, TextField } from '../ui';
import useGraphData from '../ide/graph/hooks/useGraphData';
import { classifyError } from './errors';
import PanelState from './PanelState';

/**
 * Column definitions per visualisation. `get` projects a row out of the
 * transformer output — see the inventory note: `transformArchitecture()`
 * already computes coupling and Tarjan cycle detection, so the table reuses
 * exactly the same numbers the graph draws instead of recomputing them.
 */
function nodeColumns(vizType, archMode) {
  const base = [
    { id: 'label', labelKey: 'knowledge.column.name', get: (row) => row.label },
    { id: 'path', labelKey: 'knowledge.column.path', get: (row) => row.path || row.relativePath || row.fileName || '' }
  ];

  if (vizType === 'callgraph') {
    return [
      ...base.slice(0, 1),
      { id: 'kind', labelKey: 'knowledge.column.kind', get: (row) => row.kind || '' },
      { id: 'direction', labelKey: 'knowledge.column.direction', get: (row) => row.direction || 'root' },
      { id: 'fileName', labelKey: 'knowledge.column.file', get: (row) => row.fileName || '' }
    ];
  }

  const structural = [
    ...base,
    { id: 'layer', labelKey: 'knowledge.column.layer', get: (row) => row.boundary?.layer || row.role || '' },
    { id: 'fileCount', labelKey: 'knowledge.column.files', get: (row) => row.fileCount ?? 0, numeric: true },
    { id: 'symbolCount', labelKey: 'knowledge.column.symbols', get: (row) => row.symbolCount ?? 0, numeric: true },
    { id: 'loc', labelKey: 'knowledge.column.loc', get: (row) => row.metrics?.loc ?? row.loc ?? 0, numeric: true }
  ];

  if (archMode !== 'dependencies') return structural;

  return [
    ...base,
    { id: 'layer', labelKey: 'knowledge.column.layer', get: (row) => row.boundary?.layer || row.role || '' },
    { id: 'fanIn', labelKey: 'knowledge.column.fanIn', get: (row) => row.coupling?.fanIn ?? 0, numeric: true },
    { id: 'fanOut', labelKey: 'knowledge.column.fanOut', get: (row) => row.coupling?.fanOut ?? 0, numeric: true },
    { id: 'coupling', labelKey: 'knowledge.column.coupling', get: (row) => row.coupling?.level || '' },
    { id: 'cycle', labelKey: 'knowledge.column.cycle', get: (row) => Boolean(row.coupling?.inCycle) }
  ];
}

function edgeColumns(vizType) {
  if (vizType === 'callgraph') {
    return [
      { id: 'source', labelKey: 'knowledge.column.source', get: (row) => row.sourceLabel },
      { id: 'target', labelKey: 'knowledge.column.target', get: (row) => row.targetLabel }
    ];
  }
  return [
    { id: 'source', labelKey: 'knowledge.column.source', get: (row) => row.sourceLabel },
    { id: 'target', labelKey: 'knowledge.column.target', get: (row) => row.targetLabel },
    { id: 'importCount', labelKey: 'knowledge.column.imports', get: (row) => row.importCount ?? 0, numeric: true },
    { id: 'isCycle', labelKey: 'knowledge.column.cycle', get: (row) => Boolean(row.isCycle) },
    { id: 'isCrossBoundary', labelKey: 'knowledge.column.crossBoundary', get: (row) => Boolean(row.isCrossBoundary) }
  ];
}

const COUPLING_TONE = { low: undefined, medium: 'warning', high: 'warning', critical: 'danger' };

/**
 * The mandatory tabular alternative to both graphs (spec §9.5, §12).
 *
 * Same data source as the canvas (`useGraphData` → `transformArchitecture` /
 * `transformCallGraph`), same filters, sortable columns with `aria-sort`, and
 * a roving-tabindex row cursor so the whole thing is operable from the
 * keyboard. Reached through a visible toggle, never a hidden preference.
 *
 * @param {object} props
 * @param {'architecture'|'callgraph'|'files'} props.vizType
 * @param {string|null} [props.targetSymbol]
 * @param {'structure'|'dependencies'} [props.archMode='structure']
 * @param {(archMode: string) => void} [props.onArchModeChange]
 * @param {string|null} [props.selectedId]
 * @param {(node: object) => void} [props.onSelect]
 */
export function GraphTable({
  vizType,
  targetSymbol = null,
  archMode = 'structure',
  selectedId = null,
  onSelect
}) {
  const { t, i18n } = useTranslation();
  const { nodes, edges, loading, error, noDependencyData, refetch } = useGraphData(vizType, targetSymbol, archMode);

  const [dataset, setDataset] = useState('nodes');
  const [filter, setFilter] = useState('');
  const [onlyCycles, setOnlyCycles] = useState(false);
  const [onlyHighFanIn, setOnlyHighFanIn] = useState(false);
  const [onlyCrossBoundary, setOnlyCrossBoundary] = useState(false);
  const [sort, setSort] = useState({ column: null, direction: 'ascending' });
  const [cursor, setCursor] = useState(0);
  const bodyRef = useRef(null);

  const numberFormat = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const labelById = useMemo(() => {
    const map = new Map();
    for (const node of nodes) map.set(node.id, node.data?.label || node.id);
    return map;
  }, [nodes]);

  const nodeRows = useMemo(
    () => nodes.map((node) => ({ ...node.data, id: node.id, __node: node })),
    [nodes]
  );

  const edgeRows = useMemo(
    () =>
      edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceLabel: labelById.get(edge.source) || edge.source,
        targetLabel: labelById.get(edge.target) || edge.target,
        importCount: edge.data?.importCount,
        isCycle: edge.data?.isCycle,
        isCrossBoundary: edge.data?.isCrossBoundary,
        __edge: edge
      })),
    [edges, labelById]
  );

  const columns = dataset === 'nodes' ? nodeColumns(vizType, archMode) : edgeColumns(vizType);
  const dependencyMode = vizType === 'architecture' && archMode === 'dependencies';

  const rows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    let list = dataset === 'nodes' ? nodeRows : edgeRows;

    if (needle) {
      list = list.filter((row) =>
        columns.some((column) => String(column.get(row) ?? '').toLowerCase().includes(needle))
      );
    }
    if (onlyCycles) {
      list = list.filter((row) => (dataset === 'nodes' ? row.coupling?.inCycle : row.isCycle));
    }
    if (onlyHighFanIn && dataset === 'nodes') {
      list = list.filter((row) => (row.coupling?.fanIn ?? 0) >= 5);
    }
    if (onlyCrossBoundary && dataset === 'edges') {
      list = list.filter((row) => row.isCrossBoundary);
    }

    if (sort.column) {
      const column = columns.find((candidate) => candidate.id === sort.column);
      if (column) {
        const factor = sort.direction === 'ascending' ? 1 : -1;
        list = [...list].sort((a, b) => {
          const left = column.get(a);
          const right = column.get(b);
          if (typeof left === 'number' && typeof right === 'number') return (left - right) * factor;
          if (typeof left === 'boolean' || typeof right === 'boolean') {
            return ((left ? 1 : 0) - (right ? 1 : 0)) * factor;
          }
          return String(left ?? '').localeCompare(String(right ?? ''), i18n.language) * factor;
        });
      }
    }
    return list;
  }, [
    dataset,
    nodeRows,
    edgeRows,
    columns,
    filter,
    onlyCycles,
    onlyHighFanIn,
    onlyCrossBoundary,
    sort,
    i18n.language
  ]);

  useEffect(() => {
    setCursor(0);
  }, [dataset, filter, onlyCycles, onlyHighFanIn, onlyCrossBoundary, vizType, archMode]);

  const focusRow = useCallback((index) => {
    setCursor(index);
    bodyRef.current?.querySelector(`[data-row-index="${index}"]`)?.focus();
  }, []);

  const toggleSort = (columnId) => {
    setSort((prev) =>
      prev.column === columnId
        ? { column: columnId, direction: prev.direction === 'ascending' ? 'descending' : 'ascending' }
        : { column: columnId, direction: 'ascending' }
    );
  };

  const handleKeyDown = (event, index, row) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (index < rows.length - 1) focusRow(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (index > 0) focusRow(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusRow(0);
        break;
      case 'End':
        event.preventDefault();
        focusRow(rows.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (dataset === 'nodes') onSelect?.(row.__node);
        break;
      default:
        break;
    }
  };

  const renderCell = (column, row) => {
    const value = column.get(row);
    if (typeof value === 'boolean') {
      return value ? <Badge tone="warning">{t('knowledge.yes')}</Badge> : <span>{t('knowledge.no')}</span>;
    }
    if (column.id === 'coupling' && value) {
      return <Badge tone={COUPLING_TONE[value]}>{t(`knowledge.couplingLevel.${value}`, { defaultValue: value })}</Badge>;
    }
    if (column.id === 'layer' && value) {
      return t(`knowledge.layer.${value}`, { defaultValue: value });
    }
    if (column.id === 'direction' && value) {
      return t(`knowledge.direction.${value}`, { defaultValue: value });
    }
    if (column.numeric) return numberFormat.format(Number(value) || 0);
    return value || '—';
  };

  if (loading) return <PanelState status="loading" />;
  if (error) return <PanelState status={classifyError(error)} error={error} onRetry={refetch} />;
  if (noDependencyData) {
    return <PanelState status="empty" title={t('knowledge.noDependencyData')} onRetry={refetch} />;
  }
  if (vizType === 'callgraph' && !targetSymbol) {
    return <PanelState status="empty" title={t('knowledge.callGraphNeedsSymbol')} />;
  }
  if (nodes.length === 0) {
    return <PanelState status="empty" title={t('knowledge.noGraphData')} onRetry={refetch} />;
  }

  return (
    <div className="etch-graph-table">
      <div className="etch-graph-table__controls">
        <div className="etch-graph-table__dataset" role="group" aria-label={t('knowledge.dataset')}>
          {['nodes', 'edges'].map((id) => (
            <button
              key={id}
              type="button"
              className="etch-graph-table__dataset-option"
              aria-pressed={dataset === id}
              onClick={() => setDataset(id)}
            >
              {t(id === 'nodes' ? 'knowledge.nodes' : 'knowledge.edges')}
              <span className="etch-graph-table__count">
                {numberFormat.format(id === 'nodes' ? nodeRows.length : edgeRows.length)}
              </span>
            </button>
          ))}
        </div>

        <TextField
          type="search"
          label={t('knowledge.filterRows')}
          hideLabel
          placeholder={t('knowledge.filterRows')}
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          className="etch-graph-table__filter"
        />

        {dependencyMode && (
          <div className="etch-graph-table__filters">
            <Checkbox
              label={t('knowledge.filterCycles')}
              checked={onlyCycles}
              onChange={(event) => setOnlyCycles(event.target.checked)}
            />
            {dataset === 'nodes' && (
              <Checkbox
                label={t('knowledge.filterHighFanIn')}
                checked={onlyHighFanIn}
                onChange={(event) => setOnlyHighFanIn(event.target.checked)}
              />
            )}
            {dataset === 'edges' && (
              <Checkbox
                label={t('knowledge.filterCrossBoundary')}
                checked={onlyCrossBoundary}
                onChange={(event) => setOnlyCrossBoundary(event.target.checked)}
              />
            )}
          </div>
        )}
      </div>

      <p className="etch-graph-table__status" role="status" aria-live="polite">
        {t('knowledge.rowsShown', { count: rows.length })}
      </p>

      <div className="etch-graph-table__scroll">
        <table role="grid" aria-label={t('knowledge.tableAlternative')}>
          <thead>
            <tr>
              {columns.map((column) => {
                const active = sort.column === column.id;
                const SortIcon = !active ? ArrowUpDown : sort.direction === 'ascending' ? ArrowUp : ArrowDown;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    aria-sort={active ? sort.direction : 'none'}
                    data-numeric={column.numeric || undefined}
                  >
                    <button
                      type="button"
                      className="etch-graph-table__sort"
                      onClick={() => toggleSort(column.id)}
                      aria-label={t('a11y.sortBy', { field: t(column.labelKey) })}
                    >
                      <span>{t(column.labelKey)}</span>
                      <SortIcon size={13} aria-hidden="true" />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody ref={bodyRef}>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>{t('knowledge.noSearchResults')}</td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={row.id}
                  data-row-index={index}
                  tabIndex={cursor === index ? 0 : -1}
                  aria-selected={dataset === 'nodes' ? row.id === selectedId : undefined}
                  onClick={() => {
                    setCursor(index);
                    if (dataset === 'nodes') onSelect?.(row.__node);
                  }}
                  onKeyDown={(event) => handleKeyDown(event, index, row)}
                >
                  {columns.map((column) => (
                    <td key={column.id} data-numeric={column.numeric || undefined}>
                      {renderCell(column, row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default GraphTable;
